// "NEGF - Illustrative Projects": ready-made NEGF Transport projects - graphene ribbons (conductance steps, the
// armchair width rule, I-V parity blocking), a gated ribbon (self-consistent NEGF-Poisson), edge magnetism (Hubbard U),
// a non-orthogonal fit, wires from DFT fits (diamond, ferromagnetic iron) and from Wannier90 (silicon, bismuth with
// spin-orbit coupling). Each guide says what to look for and what makes the calculation work; "try this" makes a new
// project with one setting changed. Opening one gives an NEGF project of the user's own: model ready, device and
// calculation filled in.
import { Accordion, Alert, Anchor, Badge, Button, Card, Group, Loader, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { ErrorBox } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { useMe } from "../lib/hooks";
import { openExample, tryExample, useExample, useExamples, type Example, type ExampleTry } from "../lib/examples";
import type { NegfMemory } from "../lib/negf";
import { Specialities } from "./Examples";
import { examplesPath } from "../lib/modules";

export const NEGF_EXAMPLES_TITLE = "NEGF - Illustrative Projects";

export const CALC_NAMES: Record<string, string> = {
  transmission: "T(E)", iv: "I–V", ldos: "LDOS", scf: "Self-consistent", scf_iv: "Self-consistent I–V",
  sc_conductance: "G(V), superconducting", sc_map: "G(V, X) map, superconducting", sc_abs: "Andreev levels", sc_ldos: "BdG LDOS", cb_map: "Coulomb diamonds",
};

export function NegfExamplesPage() {
  const { data: all, error, isLoading } = useExamples();
  const data = all?.filter((e) => e.product === "negf");
  const nav = useNavigate();
  return (
    <>
      <PageTitle title={NEGF_EXAMPLES_TITLE} />
      <Text size="sm" c="dimmed" mb="md" maw={900}>
        Ready-made NEGF Transport projects: graphene ribbons (conductance steps, the armchair width rule, a parity
        selection rule in the I–V), a gated ribbon solved self-consistently, magnetic edges with Hubbard U, a
        non-orthogonal fit, and wires whose models come from DFT fits and from Wannier90. Each guide says what the result
        should show and which settings make it work. Open one: the model is ready and the device and calculation are
        filled in - look at the device, run, compare; "Try this" makes a new project with one setting changed.
      </Text>
      <ErrorBox error={error} />
      {isLoading && <Loader size="sm" />}
      {data && data.length === 0 && <Text size="sm" c="dimmed">None on this server yet.</Text>}
      {data && data.length > 0 && (
        <Card withBorder p={0}>
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="sm" data-testid="negf-examples-table">
              <Table.Thead>
                <Table.Tr><Table.Th>#</Table.Th><Table.Th>Project</Table.Th><Table.Th>Device</Table.Th><Table.Th>What it shows</Table.Th>
                  <Table.Th>Calculation</Table.Th><Table.Th>Model</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((e, i) => (
                  <Table.Tr key={e.id} style={{ cursor: "pointer" }} onClick={() => nav(examplesPath("negf", e.id))}>
                    <Table.Td>{i + 1}</Table.Td>
                    <Table.Td><Link to={examplesPath("negf", e.id)} onClick={(ev) => ev.stopPropagation()}><Text fw={600} size="sm">{e.name}</Text></Link>
                      {e.project_id && <Badge size="xs" color="teal" variant="light" ml={6}>opened</Badge>}
                      {e.advanced && <Badge size="xs" color="grape" variant="light" ml={6}>Advanced resources</Badge>}</Table.Td>
                    <Table.Td><Text size="sm">{e.material}</Text></Table.Td>
                    <Table.Td><Specialities items={e.speciality} /></Table.Td>
                    <Table.Td><Text size="sm">{CALC_NAMES[e.calc ?? ""] ?? e.calc}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{e.model_source}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </>
  );
}

/** "Try this" for an NEGF illustrative project: each makes a new NEGF project with one setting changed. */
export function NegfTryList({ exampleId, tries }: { exampleId: string; tries: ExampleTry[] }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const make = useMutation({
    mutationFn: (t: string) => tryExample(exampleId, t),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      nav(`/projects/${p.id}`);
    },
  });
  if (!tries.length) return null;
  return (
    <Stack gap="xs" data-testid="negf-try-list">
      <Text fw={600}>Try this</Text>
      <Text size="xs" c="dimmed">Each makes a new NEGF Transport project with the same model and one setting changed. Run it and
        compare its result with this project's.</Text>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
        {tries.map((t) => (
          <Card key={t.id} withBorder p="xs">
            <Stack gap={4}>
              <Text size="sm" fw={600}>{t.title}</Text>
              <Text size="xs">{t.about}</Text>
              <Group><Button size="compact-xs" variant="light" loading={make.isPending && make.variables === t.id}
                onClick={() => make.mutate(t.id)} aria-label={`try ${t.id}`}>Make this project</Button></Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
      <ErrorBox error={make.error} />
    </Stack>
  );
}

function OpenButton({ ex }: { ex: Example }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: me } = useMe();
  const allowed = !!me?.limits["negf.enabled"];
  const open = useMutation({
    mutationFn: () => openExample(ex.id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["examples"] });
      nav(`/projects/${p.id}`);
    },
  });
  return (
    <Stack gap={4} align="flex-start">
      <Button onClick={() => open.mutate()} loading={open.isPending} disabled={!allowed} data-testid="open-negf-example">
        {ex.project_id ? "Continue in my project" : "Open as my project"}</Button>
      {!allowed && <Text size="xs" c="orange">NEGF Transport is not part of your plan.</Text>}
      <ErrorBox error={open.error} />
    </Stack>
  );
}

export function NegfExampleDetail() {
  const { id } = useParams();
  const { data: ex, error, isLoading } = useExample(id);
  if (isLoading) return <Loader size="sm" />;
  if (error || !ex) return <ErrorBox error={error ?? new Error("Not found")} />;
  return (
    <>
      <PageTitle title={ex.name}>
        <Button component={Link} to={examplesPath("negf")} variant="default">All NEGF illustrative projects</Button>
      </PageTitle>
      <Stack gap="md" maw={1000}>
        <Card withBorder>
          <Stack gap={6}>
            <Text size="sm"><b>Device:</b> {ex.material} · <b>Calculation:</b> {CALC_NAMES[ex.calc ?? ""] ?? ex.calc}</Text>
            <Text size="sm"><b>Model:</b> {ex.model_source}</Text>
            <Specialities items={ex.speciality} />
            <Text size="sm" c="dimmed">{ex.summary}</Text>
            {ex.expect && <Alert color="teal" p="xs" data-testid="negf-example-expect"><Text size="sm"><b>What it shows:</b> {ex.expect}</Text></Alert>}
            <OpenButton ex={ex} />
            {ex.advanced && <Alert color="grape" p="xs"><Text size="sm">This project runs with the Advanced plan's NEGF limits
              (larger devices, more energy points), granted by an administrator.</Text></Alert>}
            <Text size="xs" c="dimmed">Opening it gives you an NEGF Transport project with the model ready and the device and
              calculation filled in: check the device, then run the calculation (seconds to a minute).</Text>
          </Stack>
        </Card>
        {stepRows(ex).length > 0 && <Card withBorder><Text fw={600} mb={4}>Step by step</Text><StepTable ex={ex} /></Card>}
        <Card withBorder data-testid="example-guide"><Markdown source={ex.guide ?? ""} /></Card>
        <Card withBorder><NegfTryList exampleId={ex.id} tries={ex.tries ?? []} /></Card>
      </Stack>
    </>
  );
}

const STEP_NAMES: [string, string][] = [["model", "1 Model"], ["device", "2 Device"], ["gates", "3 Gates"], ["contacts", "4 Contacts & fields"],
  ["calc", "5 Calculation"]];

/** The example's hint for each workspace step that has one. */
export function stepRows(ex: Example): [string, string][] {
  const steps = (ex.steps ?? {}) as Record<string, string | undefined>;
  return STEP_NAMES.filter(([k]) => steps[k]).map(([k, label]) => [label, steps[k]!]);
}

function StepTable({ ex }: { ex: Example }) {
  return (
    <Table fz="sm" withRowBorders={false} data-testid="negf-example-steps">
      <Table.Tbody>
        {stepRows(ex).map(([label, text]) => (
          <Table.Tr key={label}><Table.Td w={120}><Text size="sm" fw={600}>{label}</Text></Table.Td><Table.Td><Text size="sm">{text}</Text></Table.Td></Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/** The banner of an NEGF project opened from an illustrative project (or made by its "try this"). */
export function NegfExampleBanner({ project, mem }: { project: { settings?: Record<string, unknown> | null }; mem: NegfMemory }) {
  const st = (project.settings ?? {}) as { example?: string };
  const from = mem.from_example;
  const id = st.example ?? from?.id;
  const { data: ex } = useExample(id);
  if (!id) return null;
  if (from) {
    return (
      <Alert color="grape" p="xs" data-testid="negf-from-example">
        <Text size="sm">Made from the illustrative project <Anchor component={Link} to={examplesPath("negf", from.id)}>{ex?.name ?? from.id}</Anchor>:
          {" "}<b>{from.title}</b> - {from.about}</Text>
        <Text size="xs" c="dimmed">Run it and compare with the illustrative project{ex?.expect ? ` (${ex.expect})` : ""}.</Text>
      </Alert>
    );
  }
  return (
    <Card withBorder p="sm" data-testid="negf-example-banner">
      <Stack gap={6}>
        <Text size="sm"><b>Illustrative project</b>{ex ? `: ${ex.name}` : ""}. The model is ready and the settings are filled in:
          check the device, run the calculation, compare with the guide. <Anchor component={Link} to={examplesPath("negf", id)} size="sm">All about it</Anchor></Text>
        {ex?.expect && <Text size="xs" c="dimmed"><b>What it shows:</b> {ex.expect}</Text>}
        {ex && <Accordion variant="contained" chevronPosition="left">
          <Accordion.Item value="guide">
            <Accordion.Control><Text size="sm">Guide: what to look for and the subtleties</Text></Accordion.Control>
            <Accordion.Panel><Markdown source={ex.guide ?? ""} /></Accordion.Panel>
          </Accordion.Item>
          {stepRows(ex).length > 0 && <Accordion.Item value="steps">
            <Accordion.Control><Text size="sm">Step by step</Text></Accordion.Control>
            <Accordion.Panel><StepTable ex={ex} /></Accordion.Panel>
          </Accordion.Item>}
          <Accordion.Item value="try">
            <Accordion.Control><Text size="sm">Try this ({ex.tries?.length ?? 0})</Text></Accordion.Control>
            <Accordion.Panel><NegfTryList exampleId={ex.id} tries={ex.tries ?? []} /></Accordion.Panel>
          </Accordion.Item>
        </Accordion>}
      </Stack>
    </Card>
  );
}
